import React, { useState, useEffect, Fragment } from "react";
import { clone } from "ramda";
import { makeStyles } from "@material-ui/core/styles";
import { setAspectRatio } from "../../util/mediaUtil";
import { validateThumbnailSize } from "../../util/assetUtil";

import MediaDialog from "./MediaDialog";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import PhotoOutlinedIcon from "@material-ui/icons/PhotoOutlined";
import FileUploadInput from "components/FileUploadInput";
import { isBlank, notBlank } from "../../util/ext";
import CircularProgress from "@material-ui/core/CircularProgress";
import ImageCrop from "./ImageCrop";

const imgSize = 350;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
  },

  preview: {
    marginRight: theme.spacing(4),
    minWidth: 150,
    minHeight: 200,
    display: "flex",
    alignItems: "center",
  },

  image: {
    width: 350,
    height: 350,
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    objectFit: "contain",
  },

  thumbnailOverlay: {
    height: "150px",
    width: "150px",
    position: "relative",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
  },

  emptyThumbnail: {
    fontSize: "120px",
    color: theme.palette.primary.disabled,
    position: "absolute",
    right: "15px",
    top: "15px",
  },

  input: {
    flexGrow: 1,
    maxWidth: 450,
  },

  title: {
    fontSize: "1.6rem",
    lineHeight: "1.7rem",
    color: theme.palette.primary.darker,
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(2),
  },

  subtitle: {
    fontSize: "1.2rem",
    marginBottom: 0,
  },

  fileTypes: {
    fontSize: "1.2rem",
    marginTop: 0,
    marginBottom: theme.spacing(2),
    fontStyle: "italic",
  },
}));

const NewThumbnailDialog = ({
  accept,
  media,
  onCancel,
  onSave,
  dialogTitle = "New Thumbnail",
  inputTitle = "Upload a new thumbnail",
  visible,
  uploading,
}) => {
  const classes = useStyles();
  const [error, setError] = useState();
  const [internal, setInternal] = useState({});
  const [thumbnailUrl, setThumbnailUrl] = useState();
  const [thumbnailFile, setThumbnailFile] = useState();
  const [cropper, setCropper] = useState(null);

  useEffect(() => {
    setInternal(clone(media));
  }, [media, internal.thumbnailUrl]);

  const handleCropperInitialized = cropper => setCropper(cropper);

  const onFileInputChange = file => {
    setThumbnailFile(file);
    if (notBlank(file)) {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result;
          await validateThumbnailSize(media, dataUrl);
          setThumbnailUrl(dataUrl);
        } catch (err) {
          setError(err.message);
          setThumbnailFile(null);
          setThumbnailUrl(null);
        }
      };
      reader.readAsDataURL(file);
      setError(null);
    } else {
      setThumbnailUrl(null);
      setError(null);
    }
  };

  const handleSave = () => {
    if (isBlank(thumbnailFile)) {
      setError("An image must be selected");
    } else {
      const url = cropper.getCroppedCanvas().toDataURL();
      cropper.getCroppedCanvas().toBlob(blob => {
        const file = new File([blob], thumbnailFile.name, {
          lastModified: thumbnailFile.lastModified,
        });
        onSave(url, file);
      });
    }
  };

  const handleCancel = () => {
    onCancel();
    setError(null);
    setThumbnailFile(null);
    setThumbnailUrl(null);
  };

  return (
    <MediaDialog
      open={visible}
      title={dialogTitle}
      maxWidth="lg"
      handleClose={handleCancel}
      content={
        <div className={classes.root}>
          <div className={classes.preview}>
            {uploading ? (
              <CircularProgress />
            ) : isBlank(thumbnailUrl) ? (
              <Box className={classes.thumbnailOverlay}>
                <PhotoOutlinedIcon className={classes.emptyThumbnail} />
              </Box>
            ) : (
              <ImageCrop
                aspectRatio={setAspectRatio(media)}
                thumbnailUrl={thumbnailUrl}
                currentUrl={media.thumbnailUrl}
                imgSize={imgSize}
                cropper={cropper}
                handleInitialized={handleCropperInitialized}
              />
            )}
          </div>
          <div className={classes.input}>
            <div className={classes.title}>{inputTitle}</div>
            <p className={classes.subtitle}>
              Choose the image you wish to upload.{" "}
              {media?.mediaType === "collection"
                ? "Image height should be at least 264px."
                : "The image should be 264px x 264px."}
            </p>
            <p className={classes.fileTypes}>
              Accepted file types:{" "}
              {accept ||
                ".jpg, .jpeg, .png, .gif, image/jpeg, image/png, image/gif"}
            </p>
            <FileUploadInput
              value={thumbnailFile}
              accept={
                accept ||
                ".jpg, .jpeg, .png, .gif, image/jpeg, image/png, image/gif"
              }
              onChange={onFileInputChange}
              disabled={uploading}
              error={error}
            />
          </div>
        </div>
      }
      actions={
        <Fragment>
          <Button disabled={uploading} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            disabled={uploading}
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            Save
          </Button>
        </Fragment>
      }
    />
  );
};

export default NewThumbnailDialog;
